import { FC, useEffect, useState } from 'react'
import moment from 'moment-timezone'
import { useTranslation } from 'react-i18next'
import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import { Meeting } from '../../../types'
import { useAuth } from '../../../context/auth-context'
import { useDateTime } from '../../../shared/useDateTime'
import { useScheduledMeetingsByManager } from '../../../graphql/meeting/hooks/useScheduledMeetingsByManager'

interface InviteModalMeetingOverlapProps {
  meetingDate: string
  duration: number
  meetingId?: string | undefined
}

interface InviteModalMeetingOverlapContentProps extends InviteModalMeetingOverlapProps {
  meetings: Meeting[]
}

export const InviteModalMeetingOverlap: FC<InviteModalMeetingOverlapProps> = ({ meetingDate, duration, meetingId }) => {
  const { user } = useAuth()

  const year = !!meetingDate ? new Date(meetingDate).getFullYear() : new Date().getFullYear()
  const { data, loading, error } = useScheduledMeetingsByManager(user.employeeId, year)

  if (!data || !data.scheduledMeetingsByManager || loading || !!error) {
    return null
  } else {
    return (
      <InviteModalMeetingOverlapContent
        meetingDate={meetingDate}
        duration={duration}
        meetingId={meetingId}
        meetings={data.scheduledMeetingsByManager as Meeting[]}
      />
    )
  }
}

export const InviteModalMeetingOverlapContent: FC<InviteModalMeetingOverlapContentProps> = ({
  meetingDate,
  meetings,
  meetingId,
  duration,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { datesOverlap, momentToUtcDateString } = useDateTime(user)

  const [message, setMessage] = useState<boolean>(false)

  useEffect(() => {
    const hasOverlapingMeetings = meetings
      .filter((currentMeeting) => currentMeeting.id !== meetingId)
      .some((currentMeeting) => {
        const currentMeetingEndDate = momentToUtcDateString(moment(currentMeeting.displayDate).add(currentMeeting.duration, 'seconds'))
        const plannedMeetingEndDate = momentToUtcDateString(moment(meetingDate).add(duration, 'seconds'))
        return datesOverlap(currentMeeting.displayDate, currentMeetingEndDate || '', meetingDate, plannedMeetingEndDate || '')
      })
    setMessage(hasOverlapingMeetings)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetings, meetingDate, duration])

  if (!message) {
    return null
  } else {
    return (
      <>
        <PerFlexRow mt="xs">
          <PerText color="error">{t('inviteModal.overlapMessage_1')}</PerText>
        </PerFlexRow>
        <PerFlexRow mt="xxxs">
          <PerText color="error">{t('inviteModal.overlapMessage_2')}</PerText>
        </PerFlexRow>
      </>
    )
  }
}
